import dayjs from 'dayjs';
import { MGR_COLORS } from '../config/stages';

function parsePbDate(dateStr: string): dayjs.Dayjs | null {
  if (!dateStr) return null;
  const normalized = dateStr.trim();

  // DD.MM.YYYY
  const dottedMatch = normalized.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dottedMatch) {
    const [, day, month, year] = dottedMatch;
    const d = dayjs(`${year}-${month}-${day}`);
    return d.isValid() ? d : null;
  }

  // Datetime with or without timezone — treat as UTC if no tz specified
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(normalized)) {
    const iso = normalized.replace(' ', 'T');
    const withTz = /Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
    const d = dayjs(withTz);
    return d.isValid() ? d : null;
  }

  const d = dayjs(normalized);
  return d.isValid() ? d : null;
}

export function getManagerInitials(name: string, email?: string): string {
  const normalizedName = name.trim();
  const parts = normalizedName.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const secondFromName = parts[1]?.[0] ?? '';
  const emailLocalPart = (email ?? '').trim().split('@')[0] ?? '';
  const secondFromEmail = emailLocalPart[0] ?? '';

  return (first + secondFromName + secondFromEmail).slice(0, 2).toUpperCase();
}

export function getManagerColor(value: string): string {
  const normalized = value.trim().toLowerCase();

  if (!normalized) {
    return MGR_COLORS[0];
  }

  let hash = 0;
  for (const char of normalized) {
    hash = ((hash << 5) - hash) + char.charCodeAt(0);
    hash |= 0;
  }

  return MGR_COLORS[Math.abs(hash) % MGR_COLORS.length];
}

export function pluralizeRu(count: number, one: string, few: string, many: string): string {
  const absCount = Math.abs(count);
  const lastTwoDigits = absCount % 100;
  const lastDigit = absCount % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return many;
  }

  if (lastDigit === 1) {
    return one;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return few;
  }

  return many;
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = parsePbDate(dateStr);
  return d ? d.format('DD.MM.YYYY') : dateStr;
}

export function formatShortDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = parsePbDate(dateStr);
  return d ? d.format('DD.MM') : dateStr;
}

export function formatShortDateTime(dateStr: string): string {
  if (!dateStr) return '';
  const d = parsePbDate(dateStr);
  if (!d) return dateStr;
  const time = d.format('HH:mm');
  return time === '23:59' ? `${d.format('DD.MM')} · Весь день` : `${d.format('DD.MM')} · ${time}`;
}

export function formatDateTime(dateStr: string): string {
  if (!dateStr) return '';
  const d = parsePbDate(dateStr);
  return d ? d.format('DD.MM.YYYY, HH:mm') : dateStr;
}

/** Downloads a protected file via fetch → blob URL so the token in the URL is sent as a request header. */
export async function downloadFileByUrl(url: string, filename: string): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

/** Converts a local datetime string ("YYYY-MM-DDTHH:mm") to ISO 8601 with the user's timezone offset ("YYYY-MM-DDTHH:mm:ssZ"). */
export function toLocalISOString(localStr: string): string {
  if (!localStr) return '';
  return dayjs(localStr).format();
}

export function toDateInputValue(dateStr?: string): string {
  if (!dateStr) return '';
  const d = parsePbDate(dateStr);
  return d ? d.format('YYYY-MM-DD') : '';
}
